import { Link } from '@tanstack/react-router';
import { anzeigeNummer, zweitNummer } from '@/lib/bestellnummer';
import { AgeIndicator } from './AgeIndicator';
import { ZustandMenu } from './ZustandMenu';
import { QuelleIcon } from './QuelleIcon';
import { NotizMarke } from './NotizMarke';
import { VersandartIcon } from './VersandartIcon';
import { AbholTermin } from '@/components/dashboard/AbholTermin';
import { NachlieferungMarke } from '@/components/ui/NachlieferungMarke';
import { FortschrittBadge } from '@/components/detail/FortschrittBadge';
import { ErfasserBadge } from './ErfasserBadge';
import type { BestellungZeile } from '@/types/dashboard';
import { FOKUS_RING } from '@/lib/fokus';

// Das Dashboard auf schmalen Geräten.
//
// Eine Tabelle mit zehn Spalten auf 390 Pixeln bedeutet entweder
// Querscrollen oder Schrift, die niemand liest. Stattdessen eine Karte je
// Bestellung, gestapelt, jede als Ganzes antippbar.
//
// Was auf die Karte kommt, ist dieselbe Frage wie beim Bestellzettel: nur
// das, was zum Wiedererkennen nötig ist. Nummer, Kunde, Titel, Zustand,
// das Merkzeichen für noch nicht Bestelltes, und der Termin. Die übrigen
// Spalten der Tabelle (Preis, Verlag, Anzahl) stehen in der Bestellung
// selbst — dorthin führt ein Tippen ohnehin.
//
// Die Spaltenwahl aus den Einstellungen greift hier bewusst NICHT: sie
// ordnet Spalten einer Tabelle, die es hier nicht gibt. Eine Karte, deren
// Felder einzeln abschaltbar sind, wäre eine zweite Konfiguration für
// dieselbe Sache.

/**
 * Kein Knopf im Knopf mehr (#102, A33.1).
 *
 * DER BEFUND
 *   Die ganze Karte war ein <button>, und der Zustands-Chip darin noch
 *   einmal einer. Verschachtelte Knoepfe sind kein HTML; der Browser darf
 *   das reparieren, wie er will, und Browser tun es verschieden. Auf dem
 *   Tablet ist genau das die Konstellation, in der ein Tipp beim falschen
 *   Ziel landet — und dort wird den ganzen Tag getippt.
 *
 *   Gemessen vor dem Umbau (elementFromPoint, 390 x 844): Ein Tipp in die
 *   Mitte des Chips traf zwar den Chip, hatte aber ZWEI interaktive
 *   Vorfahren — Chip und Karte. Welcher davon gewinnt, entscheidet die
 *   Fehlerbehandlung des Browsers, nicht unser Markup.
 *
 * DIE LOESUNG: EIN UEBERLAGERTER LINK
 *   Die Karte ist jetzt ein gewoehnliches <div>. Darueber liegt ein
 *   <Link>, der die ganze Flaeche abdeckt (`absolute inset-0`) — er
 *   faengt jeden Tipp, der nicht auf etwas Hoeherliegendes geht. Der
 *   Zustands-Chip liegt mit `relative z-10` darueber und faengt seinen
 *   eigenen. Damit ist der Chip kein Nachfahre des Links mehr: die
 *   Verschachtelung ist weg, nicht kaschiert.
 *
 *   Warum ein Link und kein `role="button"`-Div: Ein Div mit
 *   role="button" haette dasselbe Problem eine Ebene tiefer — auch dort
 *   sind interaktive Nachfahren unzulaessig. Der Link bringt ausserdem
 *   mit, was ein Knopf nie hatte: Adresse in der Statusleiste, Oeffnen in
 *   neuem Tab, Mittelklick.
 *
 * WAS DER UMBAU KOSTET
 *   Die Symbole in der Kopfzeile (Quelle, Versandart, Notizmarke) liegen
 *   UNTER dem Link und zeigen ihren `title` beim Zeigen nicht mehr. Das
 *   ist Absicht: Auf dem Telefon — und diese Ansicht ist die des Telefons
 *   — gibt es kein Zeigen. Wuerde man sie hochlegen, faenge jedes dieser
 *   Symbole den Tipp ab, statt die Bestellung zu oeffnen, und das waere
 *   der schlechtere Tausch. Am Schreibtisch steht die Tabelle, dort
 *   bleiben die Tooltips.
 *
 * WAS A37 DARAN GEAENDERT HAT — und was ausdruecklich NICHT
 *   Die Fussleiste ruft fuer den Abholtermin jetzt <AbholTermin>. Die
 *   Stapelung oben bleibt unangetastet: Der Link deckt weiter die ganze
 *   Flaeche, `relative z-10` liegt weiter NUR auf dem Chip und nicht auf
 *   der Zustandszeile, und AbholTermin sitzt in der Fussleiste, also
 *   unter dem Link — angetippt wird dort die Bestellung, nicht der
 *   Termin. Beim Zusammenfuehren nachgemessen, mit elementFromPoint bei
 *   390 x 844, nicht nachgelesen.
 */
function Karte({ b }: { b: BestellungZeile }) {
  const zweit = zweitNummer(b);

  return (
    <div className="relative flex w-full flex-col gap-2 rounded-md border border-line bg-cream p-3 text-left has-[a:active]:bg-cream-warm">
      {/* Die Flaeche, die in die Bestellung fuehrt. Sie steht als erstes
          Kind, damit die Vorlesereihenfolge stimmt, und traegt die
          Beschriftung fuer alle, die die Karte nicht sehen. */}
      <Link
        to="/bestellungen/$bestellnr"
        params={{ bestellnr: b.bestellnr }}
        aria-label={`Bestellung ${anzeigeNummer(b)}, ${b.kunde.anzeigename}`}
        className={`absolute inset-0 rounded-md ${FOKUS_RING}`}
      />
      <div className="flex items-baseline gap-2">
        <span className="font-serif text-akzent">{anzeigeNummer(b)}</span>
        {zweit && <span className="text-xs text-ink-faint">{zweit}</span>}
        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          {(b.hat_bestellnotiz || b.hat_positionsnotiz) && (
            <NotizMarke
              bestellnotiz={b.hat_bestellnotiz}
              positionsnotiz={b.hat_positionsnotiz}
            />
          )}
          <QuelleIcon icon={b.bestellweg.icon} label={b.bestellweg.label} />
          <VersandartIcon
            code={b.versandart?.code ?? null}
            label={b.versandart?.label ?? null}
          />
        </span>
      </div>

      <div className="min-w-0">
        <div className="truncate font-medium text-ink">{b.kunde.anzeigename}</div>
        {/* Wie in der Tabelle: die Marke NEBEN dem Titel, nicht darin
            (A45.3, #122). Gemessen auf der Karte bei 390 Pixeln, drei
            Positionen: kurzer Titel 21,3 von 21,3 Pixeln innerhalb der
            Kante, langer Titel 0 von 21,3 — und im Treffertest nicht
            mehr auffindbar. */}
        {b.haupt_position && (
          <div className="flex items-baseline gap-1 text-xs text-ink-faint">
            <span className="min-w-0 truncate">{b.haupt_position.titel}</span>
            {b.anzahl_positionen > 1 && (
              <span className="shrink-0 italic">+ {b.anzahl_positionen - 1}</span>
            )}
          </div>
        )}
        {/* A57.1 (#123): „2 von 3 da".
            WARUM HIER UND NICHT BEIM ZUSTAND: Die Marke gehoert zu den
            Titeln — „drei Titel" und „zwei Exemplare davon da" sind eine
            Aussage, und wer die Zeile darueber liest, fragt als naechstes
            genau danach. In der Zustandszeile stuende sie neben „3 offen"
            (noch keinem Beschaffungsweg zugeordnet); zwei Zahlen
            nebeneinander, die Verschiedenes meinen, liest man falsch.
            Eigene Zeile statt an den Titel gehaengt: bei 390 Pixeln
            frisst jede weitere Marke in der Titelzeile den Titel auf —
            die Lehre aus A45.3, wo genau das passiert ist. */}
        {b.fortschritt && (
          <div className="mt-1">
            <FortschrittBadge fortschritt={b.fortschritt} />
          </div>
        )}
      </div>

      {/* Zustand und Merkzeichen sitzen zusammen — dieselbe Begründung wie
          in der Tabelle: „wie weit ist sie?" und „fehlt noch etwas zu
          bestellen?" sind dieselbe Frage. */}
      {/* Ueber dem Link liegt NUR der Chip, nicht die ganze Zeile.
          Gemessen beim Umbau: Lag `z-10` auf der Zeile, fing der leere
          Platz rechts neben dem Chip den Tipp ab und tat nichts — vorher
          hatte er die Bestellung geoeffnet. Ein toter Streifen quer durch
          die Karte waere ein schlechterer Tausch als die Warnung, die wir
          loswerden wollten. */}
      <div data-tour="dashboard-zustand" className="flex flex-wrap items-center gap-1.5">
        <span className="relative z-10">
          <ZustandMenu zeile={b} />
        </span>
        {b.beschaffung_offen > 0 && (
          <span
            className="rounded bg-gold-soft px-1.5 py-0.5 text-xs tabular-nums text-gold-dark"
            title={`${b.beschaffung_offen} Exemplare sind noch keinem Beschaffungsweg zugeordnet`}
          >
            {b.beschaffung_offen} offen
          </span>
        )}
        {/* E52: Teilsendung raus, der Rest steht noch aus. */}
        {b.nachlieferung && <NachlieferungMarke art={b.nachlieferung} ebene="bestellung" />}
      </div>

      {/* Die Fußzeile steht auf JEDER Karte, auch ohne zugesagten Termin —
          dann zeigt sie das Alter. Ein Anker, den es nur manchmal gibt,
          ist keiner: die Tour hängt hier ihren Termin-Schritt hin. */}
      <div
        data-tour="dashboard-abholung"
        className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line pt-2 text-xs"
      >
        {/* A37.1 (#107): Die Fallunterscheidung steht in AbholTermin, weil
            die Tabelle dieselbe trifft — und weil sie seither DREI Fälle
            kennt. Der abgeschlossene Vorgang sagt „Termin 24.08." ohne
            Ampelpunkt, statt „überfällig (vor 5 Tagen)". */}
        {b.abholdatum_zugesagt && b.tage_bis_zugesagt !== null ? (
          /* A57.2 (#123): Auf der Karte nennt die Mahnung den Rest.
             Gemessen bei 390 Pixeln: „ueberfaellig seit 24.08. · 1 fehlt"
             misst 192,8 Pixel und steht in einer Fusszeile von 340 —
             eine Zeile, nichts abgeschnitten. */
          <AbholTermin zeile={b} restOffen={b.fortschritt?.offen ?? null} />
        ) : (
          <span className="text-ink-faint">Kein Termin</span>
        )}
        {/* A33.2 (#104): Wer hat das erfasst? Die Karte fuehrte den
            Erfasser ueberhaupt nicht — die Tabelle schon. Bei drei bis
            zehn Leuten am selben Brett ist das die schnellste Zuordnung,
            die es gibt, und ausgerechnet am Telefon fehlte sie.

            Er steht in der Fusszeile neben dem Alter, nicht oben: Die
            Karte handelt von der Bestellung, nicht vom Kollegen. Aber er
            steht dort, wo ohnehin "wann" steht — "von wem" und "seit
            wann" sind eine Frage. */}
        <span className="ml-auto flex items-center gap-3">
          <ErfasserBadge user={b.erfasser} />
          <AgeIndicator tage={b.tage_seit_erfasst} modus="seit" datum={b.erfasst_am} />
        </span>
      </div>
    </div>
  );
}

export function DashboardKarten({ zeilen }: { zeilen: BestellungZeile[] }) {
  // Der Anker sitzt am Behälter, nicht an der Liste: er muss auch dann da
  // sein, wenn gerade nichts gefunden wurde — sonst zeigt die Tour bei
  // einem strengen Filter ins Leere.
  return (
    <div data-tour="dashboard-tabelle" className="space-y-2 p-3">
      {zeilen.length === 0 ? (
        <p className="p-8 text-center text-sm text-ink-faint">Keine Bestellungen gefunden.</p>
      ) : (
        zeilen.map((b) => <Karte key={b.id} b={b} />)
      )}
    </div>
  );
}
